import { useEffect, useRef } from 'react'
import { socketSingleton } from 'services/ws'
import { ChatWebSocketMessage, TicketStatusUpdateBody, WebsocketActionsEnum } from 'types/index'
import { ChatMessage, MessageTypeEnum } from 'types/chat-message'
import { Chat, ChatResponseModeEnum } from 'types/chat'
import { store } from 'store/index'
import { messagesActions, messagesSelectors } from 'store/messages'
import { settingsActions } from 'store/settings'
import { markChatAsRead } from 'api/chats'
import { useGetBotMode } from 'hooks/useGetBotMode'

interface UseChatWebSocketOptions {
  chatId: string | undefined
  userId: string | undefined
  currentChatBotId: string | undefined
  currentUserId: string | undefined
  numberOfQuestionsBeforeAskTicketOpen: number
  onScrollToBottom?: () => void
}

/**
 * Hook to manage WebSocket rooms for chat functionality.
 * Joins the user room (UPDATE_CHAT_PARTICIPANTS) and the chat room
 * (NEW_MESSAGE, STREAM, END_STREAM); events arrive named after the action.
 */
export const useChatWebSocket = ({
  chatId,
  userId,
  currentChatBotId,
  currentUserId,
  numberOfQuestionsBeforeAskTicketOpen,
  onScrollToBottom,
}: UseChatWebSocketOptions) => {
  const botResponseCountRef = useRef(0)
  const { isAppMode } = useGetBotMode()

  // User room - personal notifications (chat participant updates)
  useEffect(() => {
    if (!userId) return
    const room = String(userId)

    const onUpdateParticipants = (wsMessage: ChatWebSocketMessage) => {
      const updatedChat = wsMessage.body as Chat
      store.dispatch(messagesActions.setChat(updatedChat))
    }

    socketSingleton.join(room)
    socketSingleton.on(WebsocketActionsEnum.UPDATE_CHAT_PARTICIPANTS, onUpdateParticipants)

    return () => {
      socketSingleton.off(WebsocketActionsEnum.UPDATE_CHAT_PARTICIPANTS, onUpdateParticipants)
      socketSingleton.leave(room)
    }
  }, [userId])

  // Chat room - messages and bot response streaming
  useEffect(() => {
    if (!chatId) return
    const room = String(chatId)

    // Reset bot response count when chat changes
    botResponseCountRef.current = 0

    const onNewMessage = (wsMessage: ChatWebSocketMessage) => {
      const message = wsMessage.body as ChatMessage
      // action events are shared across rooms: keep only this chat's messages
      if (message?.chatId !== chatId) return

      // System messages (ticket closed/reopened) have no senderId and must always be shown
      if (message?.type === MessageTypeEnum.SYSTEM) {
        store.dispatch(messagesActions.addIncomingMessage(message))
        store.dispatch(
          messagesActions.updateChat({
            lastMessage: message,
            _id: message.chatId,
          }),
        )
        return
      }

      if (message?.chatResponseMode === ChatResponseModeEnum.OPERATOR) {
        // Add message only if it's from a different user
        if (currentUserId && message.senderId !== currentUserId) {
          store.dispatch(messagesActions.addIncomingMessage(message))
        }
        store.dispatch(
          messagesActions.updateChat({
            lastMessage: message,
            _id: message.chatId,
          }),
        )
        if (isAppMode || store.getState().widgetState.isOpened) {
          markChatAsRead(chatId).catch(console.error)
        }
      }
    }

    // Auto-generated title arrives on the chat room (no chatId in body): the joined chat room
    // is the current chat, so merge the name into it; the chat list row picks it up immediately.
    const onChatNameSet = (wsMessage: ChatWebSocketMessage) => {
      const name = wsMessage.body?.name
      if (name) store.dispatch(messagesActions.updateCurrentChat({ name }))
    }

    // The stream payload carries no chatId: it belongs to this room's chat, the one this hook
    // is subscribed to. Write that chat's slice and mark it as streaming (dots off, spinner on).
    const onStream = (wsMessage: ChatWebSocketMessage) => {
      if (currentChatBotId) {
        // The server emits zero-width spaces (U+200B) as keep-alive pings while the answer is
        // still being generated. Strip them: a body that is only keep-alive carries no real
        // content, so it must not show a bubble nor end the loading dots (keep 'awaitingBot').
        const body = (wsMessage.body ?? '').replace(/\u200B/g, '')
        if (!body) return
        store.dispatch(
          messagesActions.upsertBotMessage({
            chatId,
            botMessageId: wsMessage.messageId,
            partialResponse: body,
            senderId: currentChatBotId,
          }),
        )
        store.dispatch(messagesActions.setChatStatus({ chatId, status: 'streaming' }))
      }
    }

    const onEndStream = () => {
      store.dispatch(messagesActions.setChatStatus({ chatId, status: 'idle' }))
      setTimeout(() => {
        store.dispatch(messagesActions.updateChatLastMessage())
      }, 0)

      // Check if we should show the ticket reminder
      const state = store.getState()
      const isOperatorMode = messagesSelectors.currentChatIsInOperatorMode(state)
      if (!isOperatorMode && numberOfQuestionsBeforeAskTicketOpen > 0) {
        botResponseCountRef.current += 1
        if (botResponseCountRef.current % numberOfQuestionsBeforeAskTicketOpen === 0) {
          store.dispatch(settingsActions.setTicketReminderVisible(true))
          if (onScrollToBottom) {
            setTimeout(() => {
              onScrollToBottom()
            }, 250)
          }
        }
      }
    }

    // Keep the ticket badge/state in sync without refetching (covers the reopen case too)
    const onTicketStatusUpdate = (wsMessage: ChatWebSocketMessage) => {
      const body = wsMessage.body as TicketStatusUpdateBody
      if (body?.chatId !== chatId) return
      store.dispatch(messagesActions.setTicketStatus(body))
    }

    socketSingleton.join(room)
    socketSingleton.on(WebsocketActionsEnum.NEW_MESSAGE, onNewMessage)
    socketSingleton.on(WebsocketActionsEnum.CHAT_NAME_SET, onChatNameSet)
    socketSingleton.on(WebsocketActionsEnum.STREAM, onStream)
    socketSingleton.on(WebsocketActionsEnum.END_STREAM, onEndStream)
    socketSingleton.on(WebsocketActionsEnum.TICKET_STATUS_UPDATE, onTicketStatusUpdate)

    return () => {
      socketSingleton.off(WebsocketActionsEnum.NEW_MESSAGE, onNewMessage)
      socketSingleton.off(WebsocketActionsEnum.CHAT_NAME_SET, onChatNameSet)
      socketSingleton.off(WebsocketActionsEnum.STREAM, onStream)
      socketSingleton.off(WebsocketActionsEnum.END_STREAM, onEndStream)
      socketSingleton.off(WebsocketActionsEnum.TICKET_STATUS_UPDATE, onTicketStatusUpdate)
      socketSingleton.leave(room)
    }
  }, [chatId, currentUserId, currentChatBotId, numberOfQuestionsBeforeAskTicketOpen, onScrollToBottom])
}
